import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
  useTheme,
} from '@mui/material';
import {
  BarChartIcon,
  FaceIcon,
  SettingsSharpIcon,
  StorageSharpIcon,
  TagIcon,
} from 'assets/icons';
import { NablaLogo } from 'assets/images';
import Alert from 'components/commons/Alert';
import {
  ClusterDBDialog,
  SettingDBDialog,
  TagsDialog,
} from 'components/features/SideBar';
import { PAGE_LIST } from 'constant/pages';
import React, { ReactNode, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import {
  getKeyFromLocalStorage,
  removeKeyFromLocalStorage,
  saveKeyToLocalStorage,
} from 'utils/constant';
import { postAuthLogin } from 'utils/fetch';
import ja from 'utils/ja';
export const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

interface ISidebarProps {
  open: boolean;
  handleClose: () => void;
}

interface SidebarItemsType {
  icon: ReactNode;
  label: string;
  onClick: () => void;
}

export interface ChangeDBFormType {
  dbId: string;
  dbKey: string;
}

const CHANGE_DB_FORM_DEFAULT_VALUE = {
  dbId: '',
  dbKey: '',
};

const SideBar: React.FC<ISidebarProps> = ({ open, handleClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    control,
    watch,
    reset,
    trigger,
    formState: { isValid },
  } = useForm<ChangeDBFormType>({
    defaultValues: CHANGE_DB_FORM_DEFAULT_VALUE,
  });

  const { dbId, dbKey } = watch();

  const [openDialogSettingDb, setOpenDialogSettingDb] =
    useState<boolean>(false);
  const [openDialogClusterDb, setOpenDialogClusterDb] =
    useState<boolean>(false);
  const [configError, setConfigError] = useState<boolean>(false);
  const [openDialogTag, setOpenDialogTag] = useState<boolean>(false);
  const [tags, setTags] = useState<string>(localStorage.getItem('tags') ?? '');

  const handleConfimSettingDb = () => {
    removeKeyFromLocalStorage([
      'appId',
      'appKey',
      'dbId',
      'dbKey',
      'tags',
      'url',
    ]);
    setOpenDialogSettingDb(false);
    navigate(PAGE_LIST.SETTING.pathname());
  };

  const handleConfimClusterDb = async () => {
    trigger();
    if (isValid) {
      setOpenDialogClusterDb(false);
      try {
        const appId = getKeyFromLocalStorage('appId');
        const appKey = getKeyFromLocalStorage('appKey');

        await postAuthLogin({
          appId,
          appKey,
          dbId,
          dbKey,
        });

        removeKeyFromLocalStorage(['dbId', 'dbKey']);
        saveKeyToLocalStorage('dbId', dbId);
        saveKeyToLocalStorage('dbKey', dbKey);

        const currentPath = location.pathname;
        const isHome = matchPath(PAGE_LIST.HOME.pathname(), currentPath);
        if (isHome) {
          navigate(0);
        } else {
          navigate(PAGE_LIST.HOME.pathname());
          window.location.reload();
        }
      } catch (error) {
        reset({
          dbId: getKeyFromLocalStorage('dbId') ?? '',
          dbKey: getKeyFromLocalStorage('dbKey') ?? '',
        });
        setConfigError(true);
      }
    }
  };

  useEffect(() => {
    reset({
      dbId: getKeyFromLocalStorage('dbId') ?? '',
      dbKey: getKeyFromLocalStorage('dbKey') ?? '',
    });
  }, []);

  const handleConfirmTag = () => {
    localStorage.setItem('tags', tags);
    setOpenDialogTag(false);
  };

  const handleCloseDialogClusterDb = () => {
    setOpenDialogClusterDb(false);
  };

  const sidebarItems: SidebarItemsType[] = [
    {
      icon: (
        <FaceIcon
          sx={{
            fontSize: { xs: '40px !important', sm: '60px !important' },
            fill: '#637AB9',
          }}
        />
      ),
      label: ja.side_bar.visitor_list,
      onClick: () => {
        handleClose();
        navigate(PAGE_LIST.HOME.pathname());
      },
    },
    {
      icon: (
        <BarChartIcon
          sx={{
            fontSize: { xs: '40px !important', sm: '60px !important' },
            fill: '#F3C623',
          }}
        />
      ),
      label: ja.side_bar.dashboard,
      onClick: () => {
        handleClose();
        navigate(PAGE_LIST.DASHBOARD.pathname());
      },
    },
    {
      icon: (
        <TagIcon
          sx={{
            fontSize: { xs: '40px !important', sm: '60px !important' },
            fill: '#06923E',
          }}
        />
      ),
      label: ja.side_bar.tag.title,
      onClick: () => setOpenDialogTag(true),
    },
    {
      icon: (
        <StorageSharpIcon
          sx={{
            fontSize: { xs: '40px !important', sm: '60px !important' },
            fill: '#1A96F0',
          }}
        />
      ),
      label: ja.side_bar.database,
      onClick: () => setOpenDialogClusterDb(true),
    },
    {
      icon: (
        <SettingsSharpIcon
          sx={{
            fontSize: { xs: '40px !important', sm: '60px !important' },
            fill: '#FF0000',
          }}
        />
      ),
      label: ja.side_bar.setting_db.title,
      onClick: () => setOpenDialogSettingDb(true),
    },
  ];

  return (
    <Drawer
      sx={{
        width: { xs: '80%', sm: '435px' },
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: { xs: '80%', sm: '435px' },
          boxSizing: 'border-box',
        },
      }}
      variant='temporary'
      anchor='left'
      open={open}
      onClose={handleClose}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', py: '35px' }}>
        <Box
          component='img'
          src={NablaLogo}
          alt='Logo'
          sx={{ width: { xs: '254px', sm: '314px' } }}
        />
      </Box>
      <List>
        {sidebarItems.map(({ label, icon, onClick }, index) => (
          <ListItem key={`sidebar-items-${index}`} disablePadding>
            <ListItemButton
              onClick={onClick}
              sx={{
                border: `1px solid`,
                borderColor: theme.palette.primary.main,
                borderRadius: '8px',
                boxShadow: '0px 4px 4px 0px #00000040',
                mx: '10px',
                mb: '15px',
              }}
            >
              <ListItemIcon>{icon}</ListItemIcon>
              <ListItemText
                primary={label}
                sx={{
                  textAlign: 'right',
                  '& .MuiTypography-root': {
                    fontSize: { xs: '20px !important', sm: '30px !important' },
                    fontWeight: '700',
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <SettingDBDialog
        open={openDialogSettingDb}
        onClose={() => setOpenDialogSettingDb(false)}
        handleConfirm={handleConfimSettingDb}
      />
      <TagsDialog
        open={openDialogTag}
        onClose={() => setOpenDialogTag(false)}
        handleConfirm={handleConfirmTag}
        options={[]}
        tags={tags}
        setTags={setTags}
      />
      <ClusterDBDialog
        open={openDialogClusterDb}
        onClose={handleCloseDialogClusterDb}
        handleConfirm={handleConfimClusterDb}
        control={control}
      />
      <Alert
        open={configError}
        onClose={() => setConfigError(false)}
        message={ja.message_error.app_config}
        type='error'
      />
    </Drawer>
  );
};

export default SideBar;
